import React, { useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type LensCardNarrowingFilter = { label: string; onRemove?: () => void };

type LensCardFilterIndicatorProps = {
  narrowedBy?: Array<LensCardNarrowingFilter>;
  unaffectedNote?: string;
};

const SLOT_SX = {
  alignItems: 'center',
  display: 'inline-flex',
  height: 24,
  justifyContent: 'center',
  left: 0,
  p: 0,
  position: 'absolute',
  top: -2,
  width: 24,
} as const;

function removalHint(removable: Array<LensCardNarrowingFilter>): string {
  if (removable.length === 1) {
    return ` Click to remove ${removable[0].label} from the board.`;
  }
  return removable.length ? ' Click to choose one to remove from the board.' : '';
}

export default function LensCardFilterIndicator({ narrowedBy = [], unaffectedNote }: LensCardFilterIndicatorProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const focusRing = { '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 } };

  if (narrowedBy.length) {
    const removable = narrowedBy.filter((filter) => filter.onRemove);
    const title = [
      `Narrowed by ${narrowedBy.map((filter) => filter.label).join(', ')}.${removalHint(removable)}`,
      unaffectedNote,
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <>
        <Tooltip title={title}>
          <IconButton
            aria-label={title}
            data-testid="lens-card-filtered"
            onClick={(event) => {
              if (removable.length === 1) {
                removable[0].onRemove?.();
              } else if (removable.length) {
                setMenuAnchor(event.currentTarget);
              }
            }}
            sx={{ ...SLOT_SX, ...focusRing, color: tokens.accent }}
          >
            <FilterAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} open={Boolean(menuAnchor)}>
          {removable.map((filter) => (
            <MenuItem
              key={filter.label}
              onClick={() => {
                setMenuAnchor(null);
                filter.onRemove?.();
              }}
            >
              Remove {filter.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  if (!unaffectedNote) {
    return null;
  }
  return (
    <Tooltip title={unaffectedNote}>
      <Box
        aria-label={`Filter does not apply to this card. ${unaffectedNote}`}
        component="span"
        data-testid="lens-card-unaffected"
        role="img"
        sx={{ ...SLOT_SX, ...focusRing, color: 'text.secondary' }}
        tabIndex={0}
      >
        <FilterAltOffOutlinedIcon sx={{ fontSize: 18 }} />
      </Box>
    </Tooltip>
  );
}
